/**
 * WorkspaceListSection - Main workspace table section
 * Handles workspace list display, search, and table interactions
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import WorkspaceActions from '../WorkspaceActions';

interface WorkspaceListSectionProps {
  filteredWorkspaces: any[];
  isLoading: boolean;
  isLoadingCounts: boolean;
  searchFilter: string;
  onSearchFilterChange: (value: string) => void;
  selectedWorkspaces: Set<string>;
  onWorkspaceSelect: (workspaceId: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onViewWorkspace: (workspaceId: string) => void;
  onDeleteClick: (workspace: any) => void;
  onWorkspaceUpdate: (workspaceId: string, newName: string) => void;
  onWorkspaceDelete: (workspaceId: string) => void;
  workspaceCounts: Record<string, {
    keywordCount: number;
    searchCount: number;
    aiAnalysisCount: number;
  }>;
  error: any;
}

const WorkspaceListSection: React.FC<WorkspaceListSectionProps> = ({
  filteredWorkspaces,
  isLoading,
  isLoadingCounts,
  searchFilter,
  onSearchFilterChange,
  selectedWorkspaces,
  onWorkspaceSelect,
  onSelectAll,
  onViewWorkspace,
  onDeleteClick,
  onWorkspaceUpdate,
  onWorkspaceDelete,
  workspaceCounts,
  error
}) => {
  return (
    <div className="px-6 pb-6 space-y-6">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-medium text-foreground">
            Workspaces ({filteredWorkspaces.length})
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <Input
            type="search"
            placeholder="Search workspaces..."
            value={searchFilter}
            onChange={(e) => onSearchFilterChange(e.target.value)}
            className="w-full sm:w-auto max-w-xs h-9 border-border/50 focus:border-border"
          />
        </div>
      </div>
      
      {/* Table Container */}
      <div className="w-full">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading workspaces...</span>
          </div>
        ) : (
          <table className="w-full border-separate border-spacing-0 table-auto">
            <thead>
              <tr className="border-b border-border/30">
                <th className="w-[50px] px-3 py-3 text-left">
                  <Checkbox 
                    id="select-all-workspaces" 
                    checked={selectedWorkspaces.size === filteredWorkspaces.length && filteredWorkspaces.length > 0}
                    onCheckedChange={onSelectAll}
                    aria-label="Select all workspaces" 
                  />
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">Workspaces</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">Created</th>
                <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">Stats</th>
                <th className="px-3 py-3 text-right text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkspaces.length > 0 ? (
                filteredWorkspaces.map((workspace) => (
                  <tr 
                    key={workspace.id}
                    className="border-b border-border/20 hover:bg-muted/20 transition-colors duration-150"
                  >
                    <td className="px-3 py-3">
                      <Checkbox 
                        id={`select-${workspace.id}`} 
                        checked={selectedWorkspaces.has(workspace.id)}
                        onCheckedChange={(checked) => onWorkspaceSelect(workspace.id, checked as boolean)}
                        aria-label={`Select ${workspace.name}`} 
                      />
                    </td>
                    <td className="px-3 py-3 text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{workspace.name}</span>
                        {workspace.is_default && (
                          <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                            Default
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-3 text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(workspace.created_at), { addSuffix: true })}
                    </td>
                    <td className="px-3 py-3 text-sm text-muted-foreground">
                      <div className="space-y-1">
                        <div>
                          {isLoadingCounts ? (
                            <span className="inline-flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            `${workspaceCounts[workspace.id]?.keywordCount || 0} keywords`
                          )}
                        </div>
                        <div>
                          {isLoadingCounts ? (
                            <span className="inline-flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            `${workspaceCounts[workspace.id]?.searchCount || 0} searches`
                          )}
                        </div>
                        <div>
                          {isLoadingCounts ? (
                            <span className="inline-flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Loading...
                            </span>
                          ) : (
                            `${workspaceCounts[workspace.id]?.aiAnalysisCount || 0} AI analysis`
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => onViewWorkspace(workspace.id)}
                          className="text-muted-foreground hover:text-foreground hover:bg-muted/50"
                        >
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteClick(workspace);
                          }}
                          disabled={workspace.is_default}
                          className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title={workspace.is_default ? "Cannot delete default workspace" : "Delete workspace"}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        <WorkspaceActions
                          workspace={workspace}
                          onWorkspaceUpdate={onWorkspaceUpdate}
                          onWorkspaceDelete={onWorkspaceDelete}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-3 py-16 text-center text-muted-foreground text-sm">
                    {error ? (
                      'Failed to load workspaces. Please try refreshing the page.'
                    ) : searchFilter ? (
                      `No workspaces found matching "${searchFilter}"`
                    ) : (
                      "No workspaces found."
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default WorkspaceListSection;